import * as React from 'react';
import { DropdownPopover, Outside, TCssFontWeight } from '@cian/ui-kit';
import { useIsomorphicLayoutEffect, useUpdateEffect } from '@cian/react-utils';
import { SelectDropdown } from './components/SelectDropdown';
import { IOption, TOptionValue } from './types';
import { SelectButton } from './components/SelectButton';
import { useSelectController } from './hooks';
import { handleKeyboardEvent } from './utils';

export interface ISelectProps<TValue> {
  text?: string;
  placeholder?: string;
  options: IOption[];
  value?: TValue;
  onChange(value: TValue): void;
  multiple?: boolean;
  allowEmpty?: boolean;
  buttonParams?: {
    label?: string;
    fontWeight?: TCssFontWeight;
    fullWidth?: boolean;
  };
  withArrow?: boolean;
  disabled?: boolean;
}

export function Select<TValue extends TOptionValue | TOptionValue[] = TOptionValue>(props: ISelectProps<TValue>) {
  const { text, placeholder = '', options, multiple, buttonParams, withArrow, disabled } = props;

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const { selectedItems, open, focusedIndex, actions } = useSelectController(props);

  useUpdateEffect(
    () => {
      const buttonNode = buttonRef.current;

      if (!open && buttonNode) {
        buttonNode.focus();
      }
    },
    [open],
    useIsomorphicLayoutEffect,
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      handleKeyboardEvent(e, { open, actions, multiple });
    },
    [open, actions, multiple],
  );

  const buttonText = React.useMemo(() => {
    if (text !== undefined) {
      return text;
    }

    if (!selectedItems.length) {
      return placeholder;
    }

    return options
      .reduce<TOptionValue[]>((list, option) => {
        const hasValue = selectedItems.includes(option.value);

        if (!option.isTitle && hasValue) {
          const label = list.length ? option.label.toLowerCase() : option.label;
          list.push(label);
        }

        return list;
      }, [])
      .join(', ');
  }, [text, placeholder, selectedItems, options]);

  const counter = React.useMemo(() => {
    return multiple ? selectedItems.length : 0;
  }, [multiple, selectedItems.length]);

  return (
    <Outside onOutside={actions.closeDropdown} insideRefs={[buttonRef, contentRef]}>
      <DropdownPopover
        open={open}
        content={
          <SelectDropdown
            ref={contentRef}
            options={options}
            selectedItems={selectedItems}
            multiple={multiple}
            focusedIndex={focusedIndex}
            actions={actions}
            onKeyDown={handleKeyDown}
          />
        }
      >
        <SelectButton
          open={open}
          ref={buttonRef}
          text={buttonText}
          counter={counter}
          onClick={actions.toggleDropdown}
          withArrow={withArrow}
          disabled={disabled}
          {...buttonParams}
        />
      </DropdownPopover>
    </Outside>
  );
}
